import { CameraIcon } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { RootState, useAppSelector } from '../../store';

interface ImageUploadProps {
    children?: React.ReactNode;
    imagePreview: any;
    camRemoveImage: boolean;
    handleRemoveImage?: () => void;
    handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    variant?: 'circle' | 'box';
    hasResetOption?: boolean;
    handleResetImage?: () => void;
    accept?: string;
    customSettings?: {
        [key: string]: any;
    };
}

function ImageUpload({
    children,
    imagePreview,
    camRemoveImage,
    handleRemoveImage,
    handleImageChange,
    variant = 'circle',
    hasResetOption = false,
    handleResetImage,
    accept = 'image/*',
    customSettings,
}: ImageUploadProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const functionHash = btoa(unescape(encodeURIComponent(handleImageChange.toString())));

    return (
        <div className={`flex gap-5 items-center ${variant === 'box' && 'w-full h-full'} `}>
            <div
                className={`${
                    variant === 'circle'
                        ? 'rounded-full w-[50px] h-[50px] md:w-[100px] md:h-[100px]'
                        : `${customSettings?.customWidth ?? 'w-[300px]'} ${customSettings?.customHeight ?? 'h-[100px]'}`
                }
                } relative  flex-shrink-0  border border-layout-borderColor flex justify-center items-center ${
                    customSettings?.customBackgroundColor ?? 'bg-layout-bodyBackground'
                } cursor-pointer group`}
                onClick={() => {
                    if (!hasResetOption && !imagePreview) {
                        document.getElementById('imageUpload-' + functionHash)?.click();
                    } else {
                        setIsDropdownOpen(!isDropdownOpen);
                    }
                }}
            >
                {imagePreview ? (
                    <div className={`w-full h-full relative ${variant === 'circle' && 'rounded-full'}`}>
                        <img
                            src={imagePreview}
                            alt=""
                            className={`${
                                variant === 'circle'
                                    ? 'rounded-full object-cover w-full h-full'
                                    : 'max-w-full max-h-full'
                            }`}
                        />
                        <div
                            className={`absolute ${
                                variant === 'circle' && 'rounded-full'
                            } inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 ${
                                isDropdownOpen ? 'opacity-100' : ''
                            } flex justify-center items-center`}
                        >
                            <CameraIcon id="cameraIcon" size={24} className="text-white cursor-pointer" />
                        </div>
                    </div>
                ) : (
                    <span className={`${customSettings?.customForegroundColor ?? 'text-layout-textcolor'} text-center`}>
                        <span className="hidden md:inline">
                            {i18n.translate({
                                id: 'imageUpload.chooseImage',
                                sourceName: 'Main',
                            })}{' '}
                        </span>
                        <div
                            className={`absolute inset-0 bg-black  bg-opacity-50 opacity-70 hover:opacity-100 block md:hidden ${
                                isDropdownOpen ? 'opacity-100' : ''
                            } flex justify-center items-center`}
                        >
                            <CameraIcon id="cameraIcon" size={24} className="text-white cursor-pointer" />
                        </div>
                    </span>
                )}
                {isDropdownOpen && (
                    <div
                        id="imageDropdown"
                        className="absolute bg-layout-bodyBackground min-w-[200px] w-fit top-full mt-2 left-0 bg-white border border-layout-borderColor rounded shadow-lg z-10"
                    >
                        {imagePreview ? (
                            <>
                                <button
                                    className="block px-4 py-2 bg-layout-bodyBackground text-sm text-layout-textcolor hover:bg-layout-hoverColor w-full text-left"
                                    onClick={() => {
                                        document.getElementById('imageUpload-' + functionHash)?.click();
                                        setIsDropdownOpen(false);
                                    }}
                                >
                                    {i18n.translate({
                                        id: 'imageUpload.changeImage',
                                        sourceName: 'Main',
                                    })}
                                </button>
                                {camRemoveImage && (
                                    <button
                                        className="block px-4 py-2 bg-layout-bodyBackground text-sm text-layout-textcolor hover:bg-layout-hoverColor w-full text-left"
                                        onClick={(e) => {
                                            handleRemoveImage && handleRemoveImage();
                                            setIsDropdownOpen(false);
                                        }}
                                    >
                                        {i18n.translate({
                                            id: 'imageUpload.deleteImage',
                                            sourceName: 'Main',
                                        })}
                                    </button>
                                )}
                                {hasResetOption && (
                                    <button
                                        className="block px-4 py-2 bg-layout-bodyBackground text-sm text-layout-textcolor hover:bg-layout-hoverColor w-full text-left"
                                        onClick={(e) => {
                                            handleResetImage && handleResetImage();
                                            setIsDropdownOpen(false);
                                        }}
                                    >
                                        {i18n.translate({
                                            id: 'imageUpload.resetImage',
                                            sourceName: 'Main',
                                        })}
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <button
                                    className="block px-4 py-2 bg-layout-bodyBackground text-sm text-layout-textcolor hover:bg-layout-hoverColor w-full text-left"
                                    onClick={() => {
                                        document.getElementById('imageUpload-' + functionHash)?.click();
                                        setIsDropdownOpen(false);
                                    }}
                                >
                                    {i18n.translate({
                                        id: 'imageUpload.addImage',
                                        sourceName: 'Main',
                                    })}
                                </button>
                                {hasResetOption && (
                                    <button
                                        className="block px-4 py-2 bg-layout-bodyBackground text-sm text-layout-textcolor hover:bg-layout-hoverColor w-full text-left"
                                        onClick={(e) => {
                                            setIsDropdownOpen(false);
                                            handleResetImage && handleResetImage();
                                        }}
                                    >
                                        {i18n.translate({
                                            id: 'imageUpload.resetImage',
                                            sourceName: 'Main',
                                        })}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
                <input
                    type="file"
                    accept={accept}
                    id={'imageUpload-' + functionHash}
                    className="hidden"
                    aria-label={i18n.translate({ id: 'imageUpload.chooseImage', sourceName: 'Main' }) || 'Choose image'}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            handleImageChange(e);
                        }
                    }}
                />
            </div>
            {children}
        </div>
    );
}

export default ImageUpload;
